import { error } from '@sveltejs/kit';
import type { Component } from 'svelte';

export type Post = {
	title: string;
	slug: string;
	date: string;
	description: string;
	tags: string[];
	published: boolean;
};

type PostModule = {
	default: Component;
	metadata: Post;
};

type PostRecord = Post & {
	formattedDate: string;
	component?: Component;
};

const postModules = import.meta.glob('/src/content/posts/*.md', { eager: true }) as Record<
	string,
	PostModule
>;

function formatDate(date: string) {
	return new Intl.DateTimeFormat('en', {
		day: 'numeric',
		month: 'long',
		year: 'numeric'
	}).format(new Date(date));
}

function sortPosts(posts: Post[]) {
	return posts.sort((left, right) => right.date.localeCompare(left.date));
}

export function getPosts(): PostRecord[] {
	return sortPosts(
		Object.values(postModules)
			.map(({ metadata }) => metadata)
			.filter((post) => post.published)
	).map((post) => ({
		...post,
		formattedDate: formatDate(post.date)
	}));
}

export function getPost(slug: string): PostRecord {
	const match = Object.values(postModules).find(({ metadata }) => metadata.slug === slug);

	if (!match || !match.metadata.published) {
		throw error(404, 'Post not found');
	}

	return {
		...match.metadata,
		formattedDate: formatDate(match.metadata.date),
		component: match.default
	};
}
